import { isDefined } from "@clipboard-health/util-ts";
import { Box, ButtonBase } from "@mui/material";
import { startOfDay } from "date-fns";
// eslint-disable-next-line no-restricted-imports -- needed for timezone-aware date formatting and comparison
import { formatInTimeZone, utcToZonedTime } from "date-fns-tz";
import { useCallback } from "react";

import { Text } from "../../../components/Text";

const DATE_FORMAT_PATTERN = "EEEE, MMM d";

interface StartDateSelectorProps {
  /** Start of the original shift's day */
  currentDay: Date;
  /** Start of the next day */
  nextDay: Date;
  /** Currently selected start time (used to determine which day is selected) */
  selectedDate: Date;
  /** Facility timezone for formatting */
  timezone?: string;
  /** Callback when the user selects a different date */
  onDateChange: (date: Date) => void;
}

function formatDate(date: Date, timezone?: string): string {
  if (isDefined(timezone)) {
    return formatInTimeZone(date, timezone, DATE_FORMAT_PATTERN);
  }

  // Fallback: format in local timezone using Intl
  return date.toLocaleDateString("en-US", {
    weekday: "long",
    month: "short",
    day: "numeric",
  });
}

export function StartDateSelector(props: StartDateSelectorProps) {
  const { currentDay, nextDay, selectedDate, timezone, onDateChange } = props;

  // Compare in facility timezone to correctly determine which day is selected
  const zonedSelected = isDefined(timezone) ? utcToZonedTime(selectedDate, timezone) : selectedDate;
  const zonedCurrentDay = isDefined(timezone) ? utcToZonedTime(currentDay, timezone) : currentDay;
  const isCurrentDaySelected = startOfDay(zonedSelected).getTime() === zonedCurrentDay.getTime();
  const isNextDaySelected = !isCurrentDaySelected;

  const handleCurrentDayClick = useCallback(() => {
    onDateChange(currentDay);
  }, [currentDay, onDateChange]);

  const handleNextDayClick = useCallback(() => {
    onDateChange(nextDay);
  }, [nextDay, onDateChange]);

  return (
    <Box
      sx={(theme) => ({
        display: "flex",
        alignItems: "center",
        border: `1px solid ${theme.palette.border?.base}`,
        borderRadius: theme.borderRadius?.large,
        backgroundColor: theme.palette.background.base,
      })}
    >
      <ButtonBase
        aria-label={formatDate(currentDay, timezone)}
        aria-pressed={isCurrentDaySelected}
        sx={(theme) => ({
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flex: 1,
          paddingX: theme.spacing(3),
          paddingY: theme.spacing(3),
          marginY: "-1px",
          marginLeft: isCurrentDaySelected ? "-1px" : undefined,
          borderRadius: theme.borderRadius?.large,
          border: isCurrentDaySelected ? `1px solid ${theme.palette.border?.base}` : "none",
          backgroundColor: isCurrentDaySelected ? "white" : "transparent",
          "&:focus-visible": {
            outline: `2px solid ${theme.palette.primary.main}`,
            outlineOffset: 1,
          },
        })}
        onClick={handleCurrentDayClick}
      >
        <Text
          inline
          semibold
          sx={(theme) => ({
            color: isCurrentDaySelected ? theme.palette.text.primary : theme.palette.text.disabled,
            fontSize: theme.typography.body2.fontSize,
            lineHeight: 1,
            whiteSpace: "nowrap",
          })}
          variant="body2"
        >
          {formatDate(currentDay, timezone)}
        </Text>
      </ButtonBase>
      <ButtonBase
        aria-label={formatDate(nextDay, timezone)}
        aria-pressed={isNextDaySelected}
        sx={(theme) => ({
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flex: 1,
          paddingX: theme.spacing(3),
          paddingY: theme.spacing(3),
          marginY: "-1px",
          marginRight: isCurrentDaySelected ? undefined : "-1px",
          borderRadius: theme.borderRadius?.large,
          border: isNextDaySelected ? `1px solid ${theme.palette.border?.base}` : "none",
          backgroundColor: isCurrentDaySelected ? "transparent" : "white",
          "&:focus-visible": {
            outline: `2px solid ${theme.palette.primary.main}`,
            outlineOffset: 1,
          },
        })}
        onClick={handleNextDayClick}
      >
        <Text
          inline
          semibold
          sx={(theme) => ({
            color: isNextDaySelected ? theme.palette.text.primary : theme.palette.text.disabled,
            fontSize: theme.typography.body2.fontSize,
            lineHeight: 1,
            whiteSpace: "nowrap",
          })}
          variant="body2"
        >
          {formatDate(nextDay, timezone)}
        </Text>
      </ButtonBase>
    </Box>
  );
}
